import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultDetails = () => {
  const { t } = useTranslation();
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResultDetails();
  }, [submissionId]);

  const fetchResultDetails = async () => {
    try {
      const response = await axios.get(`${API}/student/submissions/${submissionId}/results`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching result details:', error);
      navigate('/student/results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  const { submission, exam_title, detailed_results } = data;
  const correctCount = detailed_results.filter(r => r.is_correct).length;
  const scoreColor = submission.score >= 70 ? 'text-green-600' :
                     submission.score >= 50 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="result-details">
      {/* Back Button */}
      <Button
        data-testid="back-button"
        variant="outline"
        onClick={() => navigate('/student/results')}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('backToResults')}
      </Button>

      {/* Summary Card */}
      <Card className="shadow-xl border-0 bg-gradient-to-r from-cyan-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-2xl">{exam_title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className={`text-5xl font-bold ${scoreColor}`}>
                {submission.score}%
              </div>
              <p className="text-sm text-gray-600 mt-2">{t('score')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-green-600">
                {correctCount}
              </div>
              <p className="text-sm text-gray-600 mt-2">{t('correctAnswers')}</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-gray-900">
                {submission.total_questions}
              </div>
              <p className="text-sm text-gray-600 mt-2">{t('totalQuestions')}</p>
            </div>
          </div>
          <div className="mt-6 text-center text-sm text-gray-600">
            {t('submittedOn')}: {new Date(submission.submitted_at).toLocaleString()}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Results */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-gray-900">{t('viewDetails')}</h3>
        {detailed_results.map((result, index) => (
          <Card
            key={result.question_id}
            className={`shadow-lg border-2 ${
              result.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
            }`}
            data-testid={`question-result-${index}`}
          >
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <CardTitle className="text-lg flex-1">
                  <span className="font-bold text-gray-700">Q{index + 1}.</span>{' '}
                  {result.question_text}
                </CardTitle>
                {result.is_correct ? (
                  <CheckCircle className="h-8 w-8 text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-600 flex-shrink-0" />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {['A', 'B', 'C', 'D'].map(option => {
                const isCorrect = result.correct_answer === option;
                const isSelected = result.selected_answer === option;
                
                return (
                  <div
                    key={option}
                    className={`p-4 rounded-lg border-2 ${
                      isCorrect
                        ? 'border-green-500 bg-green-100'
                        : isSelected
                          ? 'border-red-500 bg-red-100'
                          : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{option}.</span>
                      <span className="flex-1">{result.options[option]}</span>
                      {isCorrect && (
                        <span className="text-xs font-semibold text-green-700 bg-green-200 px-2 py-1 rounded">
                          {t('correctAnswer')}
                        </span>
                      )}
                      {isSelected && !isCorrect && (
                        <span className="text-xs font-semibold text-red-700 bg-red-200 px-2 py-1 rounded">
                          {t('yourAnswer')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ResultDetails;